import api from '../../utils/api';
import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useMediaQuery,
  useTheme,
  ListItemIcon,
  ListItemButton,
  Divider,
} from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../features/auth/authSlice';
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ListAltIcon from '@mui/icons-material/ListAlt';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ForumIcon from '@mui/icons-material/Forum';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import NotificationsIcon from '@mui/icons-material/Notifications';
import GetAppIcon from '@mui/icons-material/GetApp';
import PeopleIcon from '@mui/icons-material/People';
import MenuBookIcon from '@mui/icons-material/MenuBook';

const Header = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    dispatch(logout());
    handleClose();
    navigate('/login');
  };

  const handleProfile = () => {
    navigate('/profile');
    handleClose();
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawerWidth = 240;

  const mobileMenu = (
    <Drawer
      variant="temporary"
      anchor="left"
      open={mobileOpen}
      onClose={handleDrawerToggle}
      ModalProps={{
        keepMounted: true,
      }}
      sx={{
        display: { xs: 'block', sm: 'none' },
        '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
      }}
    >
      <Toolbar />
      <Box sx={{ overflow: 'auto' }}>
        <List>
          <ListItem button onClick={() => {
            navigate('/techniques');
            handleDrawerToggle();
          }}>
            <ListItemIcon>
              <MenuBookIcon />
            </ListItemIcon>
            <ListItemText primary="Techniques" />
          </ListItem>

          <ListItem button onClick={() => {
            navigate('/schedule');
            handleDrawerToggle();
          }}>
            <ListItemIcon>
              <CalendarMonthIcon />
            </ListItemIcon>
            <ListItemText primary="Schedule" />
          </ListItem>
          
          <ListItem button onClick={() => {
            navigate('/roster');
            handleDrawerToggle();
          }}>
            <ListItemIcon>
              <PeopleIcon />
            </ListItemIcon>
            <ListItemText primary="Roster" />
          </ListItem>

          <ListItem button onClick={() => {
            navigate('/notifications');
            handleDrawerToggle();
          }}>
            <ListItemIcon>
              <NotificationsIcon />
            </ListItemIcon>
            <ListItemText primary="Notifications" />
          </ListItem>

          <Divider />
          
          {isAuthenticated ? (
            <>
              <ListItem disablePadding>
                <ListItemButton 
                  component="a"
                  href="https://www.gracieuniversity.com/Pages/Private/Dashboard"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ListItemIcon>
                    <VideoLibraryIcon />
                  </ListItemIcon>
                  <ListItemText primary="Video" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => { navigate('/forum'); handleDrawerToggle(); }}>
                  <ListItemIcon>
                    <ForumIcon />
                  </ListItemIcon>
                  <ListItemText primary="Forum" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => { navigate('/fitness'); handleDrawerToggle(); }}>
                  <ListItemIcon>
                    <FitnessCenterIcon />
                  </ListItemIcon>
                  <ListItemText primary="Fitness" />
                </ListItemButton>
              </ListItem>
              <Divider sx={{ my: 1 }} />
              <ListItem disablePadding>
                <ListItemButton onClick={() => { handleProfile(); handleDrawerToggle(); }}>
                  <ListItemIcon>
                    <PersonIcon />
                  </ListItemIcon>
                  <ListItemText primary="Profile" />
                </ListItemButton>
              </ListItem>
            </>
          ) : (
            <>
              <ListItem disablePadding>
                <ListItemButton onClick={() => { navigate('/login'); handleDrawerToggle(); }}>
                  <ListItemIcon>
                    <LoginIcon />
                  </ListItemIcon>
                  <ListItemText primary="Login" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => { navigate('/register'); handleDrawerToggle(); }}>
                  <ListItemIcon>
                    <PersonAddIcon />
                  </ListItemIcon>
                  <ListItemText primary="Register" />
                </ListItemButton>
              </ListItem>
            </>
          )}

          <ListItem disablePadding>
            <ListItemButton onClick={() => { handleLogout(); handleDrawerToggle(); }}>
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
    </Drawer>
  );

  const handleBackup = async () => {
    try {
      const response = await api.get('/techniques/backup/', {
        responseType: 'blob'
      });
      
      // 创建下载链接
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // 生成文件名 (gracie-tracker-backup-YYYY-MM-DD-HH-mm-ss.csv)
      const now = new Date();
      const date = now.toISOString().split('T')[0];
      const time = now.toTimeString().split(' ')[0].replace(/:/g, '-');
      link.setAttribute('download', `gracie-tracker-backup-${date}-${time}.csv`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to backup data:', error);
    }
  };

  const menuItems = [
    { text: 'Profile', icon: <AccountCircleIcon />, path: '/profile' },
    // { text: 'Roster', icon: <PeopleIcon />, path: '/roster' },
    { text: 'Notifications', icon: <NotificationsIcon />, path: '/notifications' },
    { text: 'Backup Data', icon: <GetAppIcon />, onClick: handleBackup },
    { text: 'Logout', icon: <LogoutIcon />, onClick: handleLogout },
  ];

  return (
    <AppBar position="static">
      <Toolbar>
        {isMobile && (
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
        )}

        <Typography
          variant="h6"
          component="div"
          sx={{ flexGrow: 1, cursor: 'pointer' }}
          onClick={() => navigate(isAuthenticated ? '/techniques' : '/')}
        >
          Gracie Tracker
        </Typography>

        {/* Desktop Menu */}
        {!isMobile && (
          <>
            {isAuthenticated ? (
              <>
                <Button 
                  color="inherit" 
                  component={RouterLink} 
                  to="/techniques"
                >
                  Techniques
                </Button>
                <Button 
                  color="inherit" 
                  component={RouterLink}
                  to="/schedule"
                >
                  Schedule
                </Button>
                <Button 
                  color="inherit" 
                  component={RouterLink}
                  to="/forum"
                >
                  Forum
                </Button>
                <Button 
                  color="inherit" 
                  component="a"
                  href="https://www.gracieuniversity.com/Pages/Private/Dashboard"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Video
                </Button>
                <Button 
                  color="inherit" 
                  component={RouterLink}
                  to="/fitness"
                  sx={{ display: { xs: 'none', md: 'block' } }}
                >
                  Fitness
                </Button>
                <Button 
                  color="inherit" 
                  component={RouterLink}
                  to="/roster"
                >
                  Roster
                </Button>
                {/* Profile Menu */}
                <IconButton
                  size="large"
                  onClick={handleMenu}
                  color="inherit"
                >
                  {user?.avatar ? (
                    <Avatar src={user.avatar} sx={{ width: 32, height: 32 }} />
                  ) : (
                    <AccountCircleIcon />
                  )}
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                  onClick={handleClose}
                >
                  {menuItems.map((item) => (
                    <MenuItem key={item.text} onClick={() => {
                      if (item.path) {
                        navigate(item.path);
                      } else if (item.onClick) {
                        item.onClick();
                      }
                      handleClose();
                    }}>
                      <ListItemIcon>
                        {item.icon}
                      </ListItemIcon>
                      {item.text}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            ) : (
              <>
                <Button 
                  color="inherit" 
                  onClick={() => navigate('/login')}
                  startIcon={<LoginIcon />}
                >
                  Login
                </Button>
                <Button 
                  color="inherit" 
                  onClick={() => navigate('/register')}
                  startIcon={<PersonAddIcon />}
                >
                  Register
                </Button>
              </>
            )}
          </>
        )}

        {/* Mobile Menu Drawer */}
        {mobileMenu}
      </Toolbar>
    </AppBar>
  );
};

export default Header;
